import type { SmartTeamsAvailability } from '../../db/index.js';

/**
 * Parse "HH:mm" string to minutes since midnight
 */
export const parseTimeToMinutes = (time: string): number => {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Convert minutes since midnight to pixel position
 */
export const minutesToPixels = (minutes: number, startHour: number, pixelsPerHour: number): number => {
  const startMinutes = startHour * 60;
  return ((minutes - startMinutes) / 60) * pixelsPerHour;
};

/**
 * Check if two time ranges overlap
 */
export const timesOverlap = (start1: string, end1: string, start2: string, end2: string): boolean => {
  const s1 = parseTimeToMinutes(start1);
  const e1 = parseTimeToMinutes(end1);
  const s2 = parseTimeToMinutes(start2);
  const e2 = parseTimeToMinutes(end2);
  return s1 < e2 && s2 < e1;
};

const COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
  '#14b8a6', // teal
];

/**
 * Generate consistent color for field engineer based on ID
 */
export const getFieldEngineerColor = (feId: string): string => {
  const hash = feId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return COLORS[hash % COLORS.length] ?? COLORS[0]!;
};

export interface OverlapInfo {
  column: number;
  totalColumns: number;
}

/**
 * Calculate column positions for overlapping availabilities
 * Returns a map of availability ID to column info
 */
export const calculateOverlapColumns = (
  availabilities: SmartTeamsAvailability[],
): Map<string, OverlapInfo> => {
  const result = new Map<string, OverlapInfo>();

  if (availabilities.length === 0) return result;

  // Sort by start time
  const sorted = [...availabilities].sort((a, b) => {
    return parseTimeToMinutes(a.startTime!) - parseTimeToMinutes(b.startTime!);
  });

  // Track which columns are occupied until what time
  const columnEndTimes: number[] = [];

  for (const av of sorted) {
    const startMinutes = parseTimeToMinutes(av.startTime!);
    const endMinutes = parseTimeToMinutes(av.endTime!);

    // Find first available column
    let column = 0;
    while (column < columnEndTimes.length && (columnEndTimes[column] ?? 0) > startMinutes) {
      column++;
    }

    // Assign to column
    if (column >= columnEndTimes.length) {
      columnEndTimes.push(endMinutes);
    } else {
      columnEndTimes[column] = endMinutes;
    }

    result.set(av._id!, { column, totalColumns: 0 });
  }

  // Calculate total columns for overlapping groups
  const totalColumns = columnEndTimes.length;
  for (const info of result.values()) {
    info.totalColumns = totalColumns;
  }

  return result;
};

/**
 * Get CSS positioning for an availability block
 */
export const getBlockPosition = (
  av: SmartTeamsAvailability,
  startHour: number,
  slotHeight: number,
  overlapInfo?: OverlapInfo,
): { top: number; height: number; left: string; width: string } => {
  const startMinutes = parseTimeToMinutes(av.startTime!);
  const endMinutes = parseTimeToMinutes(av.endTime!);

  const top = minutesToPixels(startMinutes, startHour, slotHeight);
  const height = Math.max(((endMinutes - startMinutes) / 60) * slotHeight, 20);

  if (!overlapInfo || overlapInfo.totalColumns <= 1) {
    return { top, height, left: '0%', width: '100%' };
  }

  const widthPercent = 100 / overlapInfo.totalColumns;
  const leftPercent = overlapInfo.column * widthPercent;

  return {
    top,
    height,
    left: `${leftPercent}%`,
    width: `${widthPercent}%`,
  };
};
